import { Controller } from "@hotwired/stimulus"
import { computePromoDiscount } from "../lib/promotions"
import { formatCurrency, conditionLabel, escapeHtml } from "../lib/format"
import { checkoutAttempt, clearCheckoutAttempt } from "../lib/checkout_attempt"
import { parseMoney, formatMoney } from "../lib/money"

const SEARCH_DEBOUNCE_MS = 300

// Owns the whole "Registrar Venda" (PDV) cart — mirrors SaleCartClient.tsx:
// the cart itself is plain JS state on this controller instance (no
// server round-trip on every add/remove/price-edit), backed by two JSON
// endpoints (live search, WhatsApp-message import) and a JSON #create that
// only navigates away on success, so a failed checkout never loses the cart.
export default class extends Controller {
  static targets = [
    "query", "results", "waPanel", "waChevron", "waText", "waImportBtn", "waResult",
    "cartCount", "cartBody", "createError"
  ]

  static values = {
    userId: String,
    searchUrl: String,
    importUrl: String,
    createUrl: String,
    logUrl: String,
    promoActive: Boolean,
    promoGenreIds: Array,
    promoFreeMaxPrice: Number,
    promoPaidPerFree: Number,
    promoEndDate: String
  }

  connect() {
    this.cart = []
    this.searchResults = []
    this.submitting = false
    this.renderCart()
  }

  disconnect() {
    clearTimeout(this.searchTimer)
  }

  toggleWhatsapp() {
    this.waPanelTarget.classList.toggle("u-hidden")
    const isOpen = !this.waPanelTarget.classList.contains("u-hidden")
    this.waChevronTarget.classList.toggle("open", isOpen)
    this.waResultTarget.classList.add("u-hidden")
    this.waResultTarget.innerHTML = ""
  }

  onQueryInput() {
    clearTimeout(this.searchTimer)
    const q = this.queryTarget.value.trim()
    if (!q) {
      this.resultsTarget.innerHTML = ""
      this.searchResults = []
      return
    }
    this.searchTimer = setTimeout(() => this.runSearch(q), SEARCH_DEBOUNCE_MS)
  }

  async runSearch(q) {
    this.resultsTarget.innerHTML = `<p class="pos-empty">Buscando…</p>`

    let books = []
    try {
      const response = await fetch(`${this.searchUrlValue}?q=${encodeURIComponent(q)}`, {
        headers: { Accept: "application/json" }
      })
      books = response.ok ? await response.json() : []
    } catch {
      books = []
    }

    this.searchResults = books
    this.renderResults(books)
  }

  renderResults(books) {
    if (books.length === 0) {
      this.resultsTarget.innerHTML = `<p class="pos-empty">Nenhum livro encontrado.</p>`
      return
    }
    this.resultsTarget.innerHTML = `
      <div class="pos-result">${books.map((book, index) => this.resultTemplate(book, index)).join("")}</div>
    `
  }

  resultTemplate(book, index) {
    const bits = [ `Nº ${book.internal_code ?? "—"}`, escapeHtml(book.author), conditionLabel(book.condition) ]
    if (book.seller_name) bits.push(escapeHtml(book.seller_name))
    if (book.quantity <= 0) bits.push("esgotado")

    return `
      <button type="button" data-action="click->sale-cart#addResult" data-index="${index}" ${book.quantity <= 0 ? "disabled" : ""}>
        <span>
          <span class="pr-t">${escapeHtml(book.title)}</span><br>
          <span class="pr-m">${bits.join(" · ")}</span>
        </span>
        <span class="t-num">${formatCurrency(book.price)}</span>
      </button>
    `
  }

  addResult(event) {
    const book = this.searchResults[Number(event.currentTarget.dataset.index)]
    if (!book || book.quantity <= 0) return
    this.cart.push({ book, salePrice: book.price })
    this.renderCart()
  }

  removeFromCart(event) {
    this.cart.splice(Number(event.currentTarget.dataset.index), 1)
    this.renderCart()
  }

  updatePrice(event) {
    const index = Number(event.currentTarget.dataset.index)
    const value = parseMoney(event.currentTarget.value)
    if (value === null) {
      this.showError("Informe um preço de venda válido, como 39,90.")
      this.renderCart()
      return
    }
    this.hideError()
    this.cart[index].salePrice = value
    this.renderCart()
  }

  applySuggestedFree(event) {
    this.cart[Number(event.currentTarget.dataset.index)].salePrice = 0
    this.renderCart()
  }

  computePromo() {
    const settings = {
      active: this.promoActiveValue,
      genreIds: this.promoGenreIdsValue.map(String),
      freeItemMaxPrice: this.promoFreeMaxPriceValue,
      paidPerFree: this.promoPaidPerFreeValue,
      endDate: this.promoEndDateValue || null
    }
    const lines = this.cart.map((item, index) => ({
      id: String(index), price: item.salePrice, quantity: 1, genreIds: (item.book.genre_ids || []).map(String)
    }))
    return computePromoDiscount(settings, lines)
  }

  renderCart() {
    this.cartCountTarget.textContent = `${this.cart.length} ${this.cart.length === 1 ? "item" : "itens"}`

    if (this.cart.length === 0) {
      this.cartBodyTarget.innerHTML = `<p class="pos-empty">Nenhum livro na venda ainda.</p>`
      return
    }

    const promo = this.computePromo()
    const subtotal = this.cart.reduce((sum, item) => sum + item.salePrice, 0)

    this.cartBodyTarget.innerHTML = `
      <div class="sale-items">${this.cart.map((item, index) => this.cartItemTemplate(item, index, promo)).join("")}</div>
      <div class="sale-total"><span>Total</span><strong>${formatCurrency(subtotal)}</strong></div>
      <button type="button" class="btn btn-primary u-w-full u-mt-18" data-action="click->sale-cart#submit" ${this.submitting ? "disabled" : ""}>
        ${this.submitting ? "Registrando…" : `Registrar ${this.cart.length} ${this.cart.length === 1 ? "venda" : "vendas"}`}
      </button>
    `
  }

  cartItemTemplate(item, index, promo) {
    const suggestedFree = (promo.freeUnitsByLineId.get(String(index)) ?? 0) > 0 && item.salePrice > 0
    const badge = suggestedFree
      ? `<div class="badge neutral">Sugestão: grátis (promoção) <button type="button" class="si-rm" data-action="click->sale-cart#applySuggestedFree" data-index="${index}">Aplicar</button></div>`
      : ""
    const meta = [ `Nº ${item.book.internal_code ?? "—"}`, conditionLabel(item.book.condition) ]
    if (item.book.seller_name) meta.push(escapeHtml(item.book.seller_name))

    return `
      <div class="sale-item">
        <div class="u-min0">
          <div class="si-t">${escapeHtml(item.book.title)}</div>
          <div class="si-m">${meta.join(" · ")}</div>
          ${badge}
          <button type="button" class="si-rm" data-action="click->sale-cart#removeFromCart" data-index="${index}">remover</button>
        </div>
        <div class="sale-qty">
          <input type="text" inputmode="decimal" value="${formatMoney(item.salePrice)}" aria-label="Preço de venda"
                 data-action="change->sale-cart#updatePrice" data-index="${index}">
        </div>
      </div>
    `
  }

  async importWhatsapp() {
    const text = this.waTextTarget.value
    if (!text.trim()) return

    this.waImportBtnTarget.disabled = true
    this.waImportBtnTarget.textContent = "Importando…"
    this.waResultTarget.classList.add("u-hidden")

    let found = []
    let missing = []
    try {
      const response = await fetch(this.importUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this.csrfToken() },
        body: JSON.stringify({ text })
      })
      const data = response.ok ? await response.json() : { found: [], missing: [] }
      found = data.found || []
      missing = data.missing || []
    } catch {
      found = []
      missing = []
    }

    this.mergeWhatsappImport(found)

    this.waImportBtnTarget.disabled = false
    this.waImportBtnTarget.textContent = "Importar para a venda"
    this.renderWaResult(found.length, missing)
  }

  // Prioriza cobrir as unidades pagas primeiro; o resto entra como grátis
  // (mesma regra do original) — descontando quanto de cada livro já está no
  // carrinho pra não duplicar se a mesma mensagem for importada de novo.
  mergeWhatsappImport(found) {
    const existingCounts = new Map()
    for (const item of this.cart) {
      existingCounts.set(item.book.id, (existingCounts.get(item.book.id) ?? 0) + 1)
    }

    for (const entry of found) {
      const already = existingCounts.get(entry.book.id) ?? 0
      const total = entry.paid + entry.free
      const toAdd = Math.max(0, total - already)
      const paidToAdd = Math.min(toAdd, entry.paid)
      const freeToAdd = toAdd - paidToAdd
      for (let i = 0; i < paidToAdd; i++) this.cart.push({ book: entry.book, salePrice: entry.book.price })
      for (let i = 0; i < freeToAdd; i++) this.cart.push({ book: entry.book, salePrice: 0 })
    }

    this.renderCart()
  }

  renderWaResult(foundCount, missing) {
    const parts = []
    if (foundCount > 0) {
      parts.push(`<span class="u-c-accent">${foundCount} livro${foundCount !== 1 ? "s" : ""} adicionado${foundCount !== 1 ? "s" : ""} à venda.</span>`)
    }
    if (missing.length > 0) {
      parts.push(`<span class="u-c-danger">Não encontrado${missing.length !== 1 ? "s" : ""}: ${missing.map((code) => `#${code}`).join(", ")}</span>`)
    }
    if (foundCount === 0 && missing.length === 0) {
      parts.push(`<span class="u-c-muted">Nenhum código de livro encontrado na mensagem.</span>`)
    }
    this.waResultTarget.innerHTML = parts.join("")
    this.waResultTarget.classList.remove("u-hidden")
  }

  async submit() {
    if (this.cart.length === 0 || this.submitting) return

    this.submitting = true
    this.hideError()
    this.renderCart()

    const items = this.cart.map((item) => ({ book_id: item.book.id, sale_price: item.salePrice }))

    try {
      const idempotency_key = await checkoutAttempt(`pos.${this.userIdValue}`, items.slice().sort((a, b) =>
        String(a.book_id).localeCompare(String(b.book_id)) || a.sale_price - b.sale_price))
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this.csrfToken() },
        body: JSON.stringify({ items, idempotency_key })
      })
      const data = await response.json()
      if (data.success) {
        clearCheckoutAttempt(`pos.${this.userIdValue}`)
        this.renderSuccess(data.count, data.code)
        return
      }
      this.showError(data.error || "Erro ao registrar vendas.")
    } catch {
      this.showError("Erro ao registrar vendas.")
    }

    this.submitting = false
    this.renderCart()
  }

  renderSuccess(count, code) {
    this.element.innerHTML = `
      <div class="panel">
        <div class="panel-body">
          <div class="pos-success">
            <span class="ok"><svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M20 6 9 17l-5-5"/></svg></span>
            <h2>Vendas registradas com sucesso!</h2>
            <p>Pedido ${escapeHtml(code)}</p>
            <p>${count} ${count === 1 ? "venda registrada" : "vendas registradas"}</p>
            <a href="${this.logUrlValue}" class="btn btn-primary">Ver todas as vendas</a>
          </div>
        </div>
      </div>
    `
  }

  showError(message) {
    this.createErrorTarget.textContent = message
    this.createErrorTarget.classList.remove("u-hidden")
  }

  hideError() {
    this.createErrorTarget.classList.add("u-hidden")
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content ?? ""
  }
}
